import { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Safe localStorage helper for mobile and cross-origin compatibility
const safeGetToken = () => {
  try {
    return localStorage.getItem('skinuse_token');
  } catch (e) {
    console.warn('localStorage read error:', e);
    return null;
  }
};

const safeSetToken = (token) => {
  try {
    localStorage.setItem('skinuse_token', token);
    return true;
  } catch (e) {
    console.warn('Could not save token to localStorage:', e);
    return false;
  }
};

const safeRemoveToken = () => {
  try {
    localStorage.removeItem('skinuse_token');
  } catch (e) {
    // Ignore errors
  }
};

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(() => safeGetToken());
  const authCheckDone = useRef(false);

  const checkAuth = useCallback(async () => {
    // Prevent duplicate auth checks
    if (authCheckDone.current) {
      return;
    }

    // CRITICAL: If returning from OAuth callback, skip the /me check
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    // Always read token from localStorage to get the latest value
    const currentToken = safeGetToken();
    
    // If no token, user is not authenticated
    if (!currentToken) {
      setUser(null);
      setToken(null);
      setLoading(false);
      authCheckDone.current = true;
      return;
    }

    // Try to get user from /me endpoint
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10s timeout

      const response = await fetch(`${BACKEND_URL}/api/auth/me`, {
        method: 'GET',
        signal: controller.signal,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${currentToken}`
        }
      });

      clearTimeout(timeoutId);

      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        setToken(currentToken);
      } else {
        // Token invalid or expired - clear it
        console.warn('Auth check failed with status:', response.status);
        setUser(null);
        safeRemoveToken();
        setToken(null);
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        console.warn('Auth check timed out');
      } else {
        console.error('Auth check failed:', error);
      }
      // On network error, keep the token but mark as not authenticated
      // This allows offline access to some features
      setUser(null);
    } finally {
      setLoading(false);
      authCheckDone.current = true;
    }
  }, []); // No dependencies - always reads fresh from localStorage

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = useCallback((userData, authToken = null) => {
    setUser(userData);
    if (authToken) {
      safeSetToken(authToken);
      setToken(authToken);
    }
    // Reset auth check flag so next checkAuth works
    authCheckDone.current = true;
  }, []);

  const logout = useCallback(async () => {
    try {
      const currentToken = safeGetToken();
      await fetch(`${BACKEND_URL}/api/auth/logout`, {
        method: 'POST',
        headers: currentToken ? { 'Authorization': `Bearer ${currentToken}` } : {}
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
    setUser(null);
    safeRemoveToken();
    setToken(null);
    authCheckDone.current = false;
  }, []);

  const updateUser = useCallback((updates) => {
    setUser(prev => prev ? { ...prev, ...updates } : null);
  }, []);

  // Derived state: user is authenticated if we have both user data AND a valid token
  const isAuthenticated = !!user && !!token;

  const value = {
    user,
    token,
    loading,
    login,
    logout,
    checkAuth,
    updateUser,
    isAuthenticated
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
